import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Faq } from '../components/Faq'
import { ReliabilityBar } from '../components/ReliabilityBar'
import { CountUp, useReveal } from '../components/marketing/motion'
import faqs from '../data/faqs.json'
import { lookup } from '../lib/demo'
import { KEYED_BASE, PUBLIC_BASE } from '../lib/publicApi'
import { usePageMeta } from '../lib/usePageMeta'

// Answer-shaped Q&A — visible on the page AND emitted as FAQPage JSON-LD (verbatim) for answer
// engines. Single source of truth is src/data/faqs.json, shared with the pure-Node prerender so the
// static HTML, the hydrated app, and the structured data can never drift. Answers describe HOW
// reliability is computed; they never quote a fabricated figure.
const HOME_FAQ = faqs.home

// Canonical dataset stats — keep in sync with public/llms-full.txt and the prerendered copy.
const STATS = [
  '60+ carriers live',
  '72,000+ sailings',
  '5,000+ lanes',
  '255 ports',
  'reliability scored to the minute',
]

interface HeroRow {
  carrier_code: string
  carrier_name: string
  on_time_pct: number
  sub: string
}

const num = (v: number | string | null | undefined) =>
  v == null ? null : typeof v === 'number' ? v : Number.parseFloat(v)

// Live hero card state: null while the placeholder renders; replaced by real /public data.
interface HeroLane {
  origin: string
  destination: string
  // Port names come from /public with the lane; the locode is the fallback, so the card never
  // headlines a blank.
  origin_name: string
  destination_name: string
  rows: HeroRow[]
}

export function HomePage() {
  usePageMeta(
    'SchedulesMCP — Ocean Carrier Schedules & Reliability',
    'See which carrier actually sails on time, before you book. Live sailing schedules from 60+ carriers: 72,000+ upcoming sailings across 5,000+ lanes and 255 ports, scored against observed departures. Search your lane free.'
  )
  // One orchestrated entrance for the marketing sections (reduced-motion disables it).
  useReveal('main')

  // Instant-render placeholder (labelled "loading live data…") — swapped for the live lane as soon
  // as the same /public endpoints ComparePage uses respond. Synthetic numbers are never shown as real.
  const [live, setLive] = useState<HeroLane | null>(null)
  useEffect(() => {
    let cancelled = false
    fetch(`${PUBLIC_BASE}/reliability/covered`)
      .then((r) => r.json())
      .then(
        async (cov: {
          data?: {
            lanes?: {
              origin: string
              destination: string
              origin_name?: string
              destination_name?: string
              obs: number
            }[]
          }
        }) => {
          const lane = cov?.data?.lanes?.[0]
          if (!lane || cancelled) return
          const rel = await fetch(
            `${KEYED_BASE}/reliability/lanes?origin=${lane.origin}&destination=${lane.destination}`
          ).then((r) => r.json())
          // On-time % only: the site proxy passes nothing else of the reliability answer (07.10).
          const carriers: {
            carrier_code: string
            carrier_name: string
            on_time_pct: number | string
            provisional: boolean
          }[] = rel?.data?.carriers ?? []
          // Confident (non-provisional) scores first — a 100% from a single observed sailing must
          // not headline the card above a carrier scored across many.
          const rows: HeroRow[] = carriers
            .filter((c) => num(c.on_time_pct) != null)
            .sort((a, b) => Number(a.provisional) - Number(b.provisional))
            .slice(0, 4)
            .map((c) => ({
              carrier_code: c.carrier_code,
              carrier_name: c.carrier_name,
              on_time_pct: Math.round(num(c.on_time_pct) ?? 0),
              sub: `${c.carrier_code}${c.provisional ? ' · early data' : ''}`,
            }))
          if (rows.length > 0 && !cancelled)
            setLive({
              origin: lane.origin,
              destination: lane.destination,
              origin_name: lane.origin_name ?? lane.origin,
              destination_name: lane.destination_name ?? lane.destination,
              rows,
            })
        }
      )
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const placeholder: HeroRow[] = lookup('CNSHA', 'NLRTM')
    .slice(0, 4)
    .map((c) => ({
      carrier_code: c.carrier_code,
      carrier_name: c.carrier_name,
      on_time_pct: c.on_time_pct,
      sub: `${c.carrier_code} · ${c.transit_p50_days}d transit`,
    }))
  const rows = live?.rows ?? placeholder
  // The card is headlined by the port NAMES, with the UN/LOCODEs kept next to them: the codes are what
  // the API and a forwarder's booking speak, but they are not what a reader can parse at a glance.
  const laneTitle = live ? `${live.origin_name} → ${live.destination_name}` : 'Shanghai → Rotterdam'
  const laneCodes = live ? `${live.origin} → ${live.destination}` : 'CNSHA → NLRTM'

  return (
    <main className="mx-auto w-full px-6" style={{ maxWidth: 'min(1240px, 100vw - 48px)' }}>
      {/* Hero — book §07 anatomy: mono teal eyebrow → ultralight one-line title → one-line hook → instrument */}
      <section className="pt-24 pb-14 text-center">
        <p
          className="m-0 font-mono text-xs uppercase text-accent"
          style={{ letterSpacing: '0.14em' }}
        >
          Pre-booking intelligence
        </p>
        <h1
          className="mx-auto mt-10 mb-0 text-[clamp(30px,5.2vw,54px)] leading-[1.06] text-[var(--text-primary)] font-display"
          style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
        >
          See which carrier actually sails on time
        </h1>
        <p className="mx-auto mt-10 mb-0 text-base leading-[1.5] text-[var(--text-secondary)]">
          Live sailing schedules from 60+ carriers, scored against the departures they actually
          made, not the ones they promised.
        </p>
        <div className="mt-14 flex items-center justify-center gap-4">
          <Link
            to="/compare"
            className="bg-signal text-on-accent font-bold px-6 py-3 rounded-xl no-underline hover:opacity-90"
          >
            Search your lane, free
          </Link>
          <Link
            to="/mcp"
            className="border border-border text-[var(--text-primary)] px-6 py-3 rounded-xl no-underline hover:border-signal/50"
          >
            Add to Claude / Cursor
          </Link>
        </div>
      </section>

      {/* Stats bar — live dataset totals, counted up once */}
      <section className="mb-24">
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 font-mono text-xs text-[var(--text-secondary)] [font-variant-numeric:tabular-nums]">
          {STATS.map((s, i) => (
            <span key={s} className="flex items-center gap-3">
              {i > 0 && (
                <span aria-hidden className="text-[var(--text-muted)]">
                  ·
                </span>
              )}
              <CountUp value={s} />
            </span>
          ))}
        </div>
      </section>

      {/* Live lane card — same /public endpoints as the Compare page, no auth. */}
      <section
        className="max-w-3xl mx-auto bg-surface-0 border border-border rounded-2xl p-6 mb-24"
        aria-busy={!live}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display font-bold text-lg">
            {laneTitle}
            {laneCodes !== laneTitle && (
              <span className="ml-2 font-mono text-xs font-normal text-[var(--text-muted)]">
                {laneCodes}
              </span>
            )}
          </h2>
          <span className="text-xs font-mono text-[var(--text-muted)]">
            {live ? 'live · 90-day on-time %' : 'loading live data…'}
          </span>
        </div>
        <div className={`space-y-4 ${live ? '' : 'opacity-50'}`}>
          {rows.map((c) => (
            <div key={c.carrier_code} className="flex items-center justify-between gap-6">
              <div className="min-w-[140px]">
                <div className="font-medium">{c.carrier_name}</div>
                <div className="text-xs font-mono text-[var(--text-muted)]">{c.sub}</div>
              </div>
              <ReliabilityBar pct={c.on_time_pct} />
            </div>
          ))}
        </div>
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <Link
            to={
              live ? `/compare?origin=${live.origin}&destination=${live.destination}` : '/compare'
            }
            className="text-sm text-signal no-underline hover:underline"
          >
            Compare any lane →
          </Link>
          {!live && (
            <span className="text-xs text-[var(--text-muted)]">
              placeholder shown while the live engine responds
            </span>
          )}
        </div>
      </section>

      {/* Differentiators */}
      <section className="grid md:grid-cols-3 gap-6 mb-24">
        {[
          {
            t: 'When one source blocks, the schedule still arrives',
            d: 'Every connector has 5 fallback transport paths: from direct API calls to hardened browser fetching. A blocked or rate-limited source fails over instead of leaving your lane empty.',
          },
          {
            t: 'Minute-level ground truth',
            d: 'Reliability is measured against the schedule the carrier FIRST declared, not the quietly re-published one, with actual berth times observed down to the minute.',
          },
          {
            t: 'Built for AI agents',
            d: '6 MCP tools on a remote server with sub-second cached responses, plus an open REST API. The same data backbone powers TrackingMCP container tracking.',
          },
        ].map((f) => (
          <div key={f.t} className="cp-card bg-surface-0 border border-border rounded-2xl p-6">
            <h3 className="font-display font-bold text-lg mb-2">{f.t}</h3>
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed text-justify [hyphens:auto]">
              {f.d}
            </p>
          </div>
        ))}
      </section>

      {/* Reliability proof — a real measured lane, not a marketing number. */}
      <section className="max-w-3xl mx-auto mb-24">
        <div className="bg-surface-0 border border-[var(--border-strong)] rounded-2xl p-8 text-center">
          <span
            className="inline-block text-xs font-mono uppercase text-accent mb-4"
            style={{ letterSpacing: '0.14em' }}
          >
            Measured, not promised
          </span>
          <p className="font-display font-light text-xl md:text-2xl leading-snug mb-3 tracking-[-0.015em]">
            On Shanghai → Laem Chabang, one major carrier hit its declared sailing date{' '}
            <span className="font-mono font-semibold text-amber [font-variant-numeric:tabular-nums]">
              12% of the time
            </span>
            . Average slip{' '}
            <span className="font-mono font-semibold text-amber [font-variant-numeric:tabular-nums]">
              7 days
            </span>
            .
          </p>
          <p className="text-sm text-[var(--text-secondary)] mb-6">
            Scored from 17 observed sailings against the first-declared schedule.
          </p>
          <Link
            to="/compare?origin=CNSHA&destination=THLCH"
            className="inline-block bg-signal text-on-accent font-bold px-6 py-3 rounded-xl no-underline hover:opacity-90"
          >
            Check your lane before you sign the booking
          </Link>
        </div>
      </section>

      {/* Definitions LLMs can quote directly */}
      <section className="max-w-3xl mx-auto mb-24">
        <h2 className="font-display font-light text-3xl mb-6 text-center tracking-[-0.015em]">
          Ocean carrier schedules & reliability, defined
        </h2>
        <dl className="space-y-5">
          <div>
            <dt className="font-medium text-[var(--text-primary)]">What is a sailing schedule?</dt>
            <dd className="text-sm text-[var(--text-secondary)] leading-relaxed mt-1">
              A carrier's planned point-to-point departures on a lane: the vessel, voyage, service
              loop, cut-off, departure and arrival dates for moving a container from one port to
              another.
            </dd>
          </div>
          <div>
            <dt className="font-medium text-[var(--text-primary)]">
              What is on-time reliability (schedule reliability)?
            </dt>
            <dd className="text-sm text-[var(--text-secondary)] leading-relaxed mt-1">
              The share of a carrier's sailings that arrive on schedule, measured from actual versus
              scheduled arrivals across tracked port calls, not from the carrier's own promises.
            </dd>
          </div>
          <div>
            <dt className="font-medium text-[var(--text-primary)]">What is SchedulesMCP?</dt>
            <dd className="text-sm text-[var(--text-secondary)] leading-relaxed mt-1">
              A pre-booking decision tool that compares ocean carriers on any lane by transit time
              and observed on-time reliability, available as a web app, a REST API, and a hosted MCP
              server for AI agents.
            </dd>
          </div>
        </dl>
      </section>

      <Faq id="home" items={HOME_FAQ} />

      {/* Closing CTA */}
      <section className="text-center pt-24 pb-24">
        <h2 className="font-display font-light text-3xl mb-5 tracking-[-0.015em]">
          Pick your carrier before you book
        </h2>
        <div className="flex items-center justify-center gap-4">
          <Link
            to="/compare"
            className="bg-signal text-on-accent font-bold px-6 py-3 rounded-xl no-underline hover:opacity-90"
          >
            Search your lane, free
          </Link>
          <Link
            to="/pricing"
            className="border border-border text-[var(--text-primary)] px-6 py-3 rounded-xl no-underline hover:border-signal/50"
          >
            See plans
          </Link>
        </div>
      </section>
    </main>
  )
}
